import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Registry } from './registry.jsx';
import { getModuleMeta } from './moduleCatalog';

function resolveStyle(props) {
  if (!props) return undefined;
  const style = {};
  if (props.background === 'surface') style.background = 'var(--pb-surface, #f8fafc)';
  if (props.background && props.background.startsWith('#')) style.background = props.background;
  if (props.color) style.color = props.color;
  if (props.textAlign) style.textAlign = props.textAlign;
  if (props.fontSize) style.fontSize = typeof props.fontSize === 'number' ? props.fontSize+'px' : props.fontSize;
  if (props.maxWidthPx) style.maxWidth = props.maxWidthPx + 'px';
  if (props.padding) {
    const map = { none: '0', sm: '0.5rem', md: '1rem', lg: '2rem', xl: '3rem' };
    style.padding = map[props.padding] || props.padding;
  }
  return style;
}

function EditableBox({ node, locked, selected, hovered, onSelect, onDelete, onDuplicate, onOpenSettings, onUpdateProps, dragAttributes, dragListeners, children }){
  const border = selected
    ? 'ring-2 ring-sky-500 shadow-[0_0_0_1px_rgba(56,189,248,0.4)]'
    : 'ring-1 ring-slate-200 hover:ring-sky-400 hover:shadow-[0_0_0_1px_rgba(148,163,184,0.6)]';
  const showControls = selected || hovered;
  const isInlineEditable = ['heading', 'text'].includes(node.type);
  return (
    <div
      className={`relative rounded-md bg-background/80 backdrop-blur ${border} transition-shadow transition-transform duration-150 ${selected ? 'scale-[1.01]' : 'hover:scale-[1.003]'} cursor-default`}
      onClick={(e)=>{ e.stopPropagation(); onSelect?.(node.id); onOpenSettings?.(node.id); }}
    >
      <div className={`absolute -top-2 left-2 z-20 rounded-full border border-slate-200 bg-slate-900/90 text-white/90 px-2 py-[1px] text-[9px] tracking-wide flex items-center gap-1 shadow-sm transition ${showControls ? '' : 'opacity-0 pointer-events-none'}`}>
        <span className="uppercase opacity-70">{node.type}</span>
        {locked && <span className="text-[8px] px-1 rounded-full bg-amber-500/20 text-amber-200 border border-amber-500/50">LOCKED</span>}
        {isInlineEditable && selected && <span className="text-[8px] px-1 rounded-full bg-blue-500/20 text-blue-200 border border-blue-500/50">Doppelklick zum Bearbeiten</span>}
      </div>
      <div className={`absolute -top-2 right-2 z-20 flex gap-1 transition ${showControls ? '' : 'opacity-0 pointer-events-none'}`}>
        <div
          title="Ziehen zum Verschieben"
          className="text-[10px] px-1 rounded-full border bg-slate-900/90 border-slate-600 text-slate-100 cursor-grab active:cursor-grabbing select-none shadow-sm hover:bg-slate-800"
          {...dragAttributes}
          {...dragListeners}
          onClick={(e)=> e.stopPropagation()}
        >
          ⠿
        </div>
        <button
          disabled={locked}
          title={locked ? 'Pflicht – nicht duplizierbar' : 'Duplizieren'}
          onClick={(e)=>{ e.stopPropagation(); if(!locked) onDuplicate?.(node.id); }}
          className={`text-[10px] px-1 rounded-full border ${locked? 'opacity-40 cursor-not-allowed bg-slate-800/70 border-slate-700 text-slate-400' : 'bg-slate-900/90 hover:bg-slate-800 border-slate-600 text-white shadow-sm'}`}
        >⧉</button>
        {/* Einstellungen Button entfernt: Einstellungen öffnen jetzt automatisch beim Klick auf das Modul */}
        <button disabled={locked} title={locked? 'Pflicht – nicht löschbar' : 'Löschen'}
          onClick={(e)=>{ e.stopPropagation(); if(!locked) onDelete?.(node.id); }}
          className={`text-[10px] px-1 rounded-full border ${locked? 'opacity-40 cursor-not-allowed bg-slate-800/70 border-slate-700 text-slate-400' : 'bg-red-500/90 hover:bg-red-400 border-red-400 text-white shadow-sm'}`}>✕</button>
      </div>
      {/*
        Important: Only disable pointer events for leaf content to keep inline DnD smooth.
        When rendering recursive children (containers like card/section/container/group),
        we must allow pointer events so child drag handles are usable.
      */}
      {children ? (
        <div className="p-2 relative z-0">
          {children}
        </div>
      ) : (
        <div className="p-2 relative z-0 pointer-events-auto">
          <NodeContent node={node} onUpdateProps={onUpdateProps} />
        </div>
      )}
    </div>
  );
}

function AddMenu({ onAdd, label }){
  const [open, setOpen] = React.useState(false);
  return (
    <div className="relative">
      <button className="text-[10px] px-1 rounded border bg-background hover:bg-blue-50 border-blue-300 text-blue-700" onClick={(e)=>{ e.stopPropagation(); setOpen(v=>!v); }}>{label}</button>
      {open && (
        <div className="absolute right-0 mt-1 bg-background border rounded shadow text-[11px] z-20 max-h-52 overflow-auto">
          <AddMenuContent onChoose={(t)=>{ setOpen(false); onAdd?.(t); }} />
        </div>
      )}
    </div>
  );
}

// This will be replaced by injected allowed list via context
const AllowedContext = React.createContext({ allowed: [], locked: [] });

function AddMenuContent({ onChoose }){
  const { allowed } = React.useContext(AllowedContext);
  return (
    <div className="p-1 min-w-44">
      {allowed.map(t => (
        <button key={t} onClick={(e)=>{ e.stopPropagation(); onChoose(t); }} className="block w-full text-left px-2 py-1 rounded hover:bg-slate-100">
          {t}
        </button>
      ))}
    </div>
  );
}

function NodeContent({ node, onUpdateProps }){
  const Comp = Registry[node.type];
  if(!Comp) return <div className="text-xs text-red-600">Unbekannter Typ: {node.type}</div>;
  const children = node.children?.map((c,i)=> <React.Fragment key={c.id || i}><div data-edit-child={c.id} /></React.Fragment>);
  
  // Enable inline editing for text and heading types
  const editable = ['heading', 'text'].includes(node.type);
  
  return <Comp props={node.props} editable={editable} onUpdate={onUpdateProps}>{children}</Comp>;
}

function SortableChild({ child, parentId, selectedId, setSelectedId, hoveredId, setHoverTargetId, onDelete, onDuplicate, onOpenSettings, onUpdateNodeProps, lockedTypes, onReorder, onAddChild, onAddSibling, openAddModal, isInColumn }){
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: child.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const locked = lockedTypes.includes(child.type);
  return (
    <div ref={setNodeRef} style={style}
      className={`${isInColumn ? '' : 'mb-2'} ${isDragging? 'opacity-50' : ''}`}
      onMouseEnter={()=> setHoverTargetId?.(child.id)}
      onMouseLeave={()=> setHoverTargetId?.(null)}
    >
      <EditableBox
        node={child}
        locked={locked}
        selected={selectedId===child.id}
        hovered={hoveredId===child.id}
        onSelect={setSelectedId}
        onDelete={onDelete}
        onDuplicate={onDuplicate}
        onOpenSettings={onOpenSettings}
        onUpdateProps={(newProps) => onUpdateNodeProps?.(child.id, newProps)}
        dragAttributes={attributes}
        dragListeners={listeners}>
        {/* recurse into child */}
        <EditableParent node={child} selectedId={selectedId} setSelectedId={setSelectedId}
          hoveredId={hoveredId} setHoverTargetId={setHoverTargetId}
          onReorder={onReorder}
          onAddChild={onAddChild}
          onAddSibling={onAddSibling}
          onDelete={onDelete}
          onOpenSettings={onOpenSettings}
          onUpdateNodeProps={onUpdateNodeProps}
          openAddModal={openAddModal}
          lockedTypes={lockedTypes} />
      </EditableBox>
    </div>
  );
}

export function EditableRenderer({ tree, selectedId, setSelectedId, onReorder, onAddChild, onAddSibling, onDelete, onDuplicate, onOpenSettings, onUpdateNodeProps, allowedComponents, lockedTypes=['codeRequestForm','codeDisplay'] }){
  if (!tree?.root) return <div className="text-xs text-slate-500">Empty</div>;
  const [addReq, setAddReq] = React.useState(null);
  const [hoverTargetId, setHoverTargetId] = React.useState(null);
  const [hoveredId, setHoveredId] = React.useState(null);
  React.useEffect(()=>{
    const t = setTimeout(()=> setHoveredId(hoverTargetId), 100);
    return ()=> clearTimeout(t);
  }, [hoverTargetId]);
  function openAddModal(payload){ setAddReq(payload); }
  function closeAddModal(){ setAddReq(null); }
  return (
  <AllowedContext.Provider value={{ allowed: (allowedComponents && allowedComponents.length ? allowedComponents : Object.keys(Registry)), locked: lockedTypes }}>
      <div className="pb-editable-root">
        <EditableParent node={tree.root} selectedId={selectedId} setSelectedId={setSelectedId}
          hoveredId={hoveredId} setHoverTargetId={setHoverTargetId}
          onReorder={onReorder}
          onAddChild={onAddChild}
          onAddSibling={onAddSibling}
          onDelete={onDelete}
          onOpenSettings={onOpenSettings}
          onUpdateNodeProps={onUpdateNodeProps}
          openAddModal={openAddModal}
          lockedTypes={lockedTypes}
        />
        {addReq && (
          <AddModuleModal
            onClose={closeAddModal}
            onChoose={(type)=>{
              if(addReq.mode==='sibling'){
                onAddSibling?.(addReq.parentId, addReq.refChildId, type, addReq.position);
              } else if(addReq.mode==='child'){
                onAddChild?.(addReq.parentId, type);
              }
              closeAddModal();
            }}
          />
        )}
      </div>
    </AllowedContext.Provider>
  );
}

function EditableParent({ node, selectedId, setSelectedId, hoveredId, setHoverTargetId, onReorder, onAddChild, onAddSibling, onDelete, onDuplicate, onOpenSettings, onUpdateNodeProps, openAddModal, lockedTypes }){
  const Comp = Registry[node.type] || (({children})=> <div>{children}</div>);
  const isColumnLayout = node.type && node.type.startsWith('columns');
  const canHaveChildren = ['section','container','card','row','codeRequestGroup'].includes(node.type) || isColumnLayout;
  const hasChildren = node.children && node.children.length > 0;
  
  // Special rendering for column layouts
  if (isColumnLayout) {
    const childrenArray = (node.children || []).map((child, idx) => (
      <div key={child.id}>
        <SortableContext items={[child.id]} strategy={verticalListSortingStrategy}>
          <Drop id={`drop-child:${node.id}-${idx}`} label={!child ? 'Hier hineinziehen' : ''} />
          <SortableChild child={child} parentId={node.id}
            selectedId={selectedId} setSelectedId={setSelectedId}
            hoveredId={hoveredId} setHoverTargetId={setHoverTargetId}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
            onOpenSettings={onOpenSettings}
            onUpdateNodeProps={onUpdateNodeProps}
            onReorder={onReorder}
            onAddChild={onAddChild}
            onAddSibling={onAddSibling}
            openAddModal={openAddModal}
            lockedTypes={lockedTypes}
            isInColumn={true}
          />
        </SortableContext>
      </div>
    ));
    
    return (
      <div className="pb-parent group">
        <Comp props={node.props}>{childrenArray}</Comp>
      </div>
    );
  }
  
  return (
    <div className="pb-parent group">
      <Comp props={node.props}>
        <SortableContext items={(node.children||[]).map(c=>c.id)} strategy={verticalListSortingStrategy}>
          {/* Drop zone at the beginning (always visible for containers) */}
          {canHaveChildren && <Drop id={`drop-child:${node.id}`} label={hasChildren ? '' : 'Hier Module hineinziehen'} />}
          
          {(node.children||[]).map((child, idx, arr) => (
            <div key={child.id}>
              {/* Inline add BEFORE each child */}
              <InlineAddSlot parentId={node.id} refChildId={child.id} position="before" openAddModal={openAddModal} />
              <Drop id={`drop-before:${child.id}`} />
              <SortableChild child={child} parentId={node.id}
                selectedId={selectedId} setSelectedId={setSelectedId}
                hoveredId={hoveredId} setHoverTargetId={setHoverTargetId}
                onDelete={onDelete}
                onDuplicate={onDuplicate}
                onOpenSettings={onOpenSettings}
                onUpdateNodeProps={onUpdateNodeProps}
                onReorder={onReorder}
                onAddChild={onAddChild}
                onAddSibling={onAddSibling}
                openAddModal={openAddModal}
                lockedTypes={lockedTypes}
                isInColumn={isColumnLayout}
              />
              {/* After last child, add a trailing inline add slot and drop zone */}
              {idx===arr.length-1 && (
                <>
                  <InlineAddSlot parentId={node.id} refChildId={child.id} position="after" openAddModal={openAddModal} />
                  <Drop id={`drop-after:${child.id}`} />
                  {/* Additional drop zone at the end for easier dropping INTO the container */}
                  <Drop id={`drop-child-end:${node.id}`} label="Hier hinzufügen" />
                </>
              )}
              {idx !== arr.length-1 && <Drop id={`drop-after:${child.id}`} />}
            </div>
          ))}
        </SortableContext>
        {canHaveChildren && (!node.children || node.children.length===0) && (
          <EmptyAddSlot parentId={node.id} openAddModal={openAddModal} />
        )}
      </Comp>
    </div>
  );
}

function InlineAddSlot({ parentId, refChildId, position, openAddModal }){
  const { t } = useTranslation();
  return (
    <div className="relative my-0 h-0 group/inlineAdd">
      {/* Hover overlay that does not affect layout */}
      <div className="absolute -top-2 left-0 right-0 h-4 z-30 pointer-events-auto group/inlineAdd">
        {/* dashed divider line (visible only on hover of overlay) */}
        <div className="absolute inset-0 flex items-center pointer-events-none" aria-hidden>
          <div className="w-full border-t border-dashed border-slate-300 opacity-0 transition-opacity duration-100 group-hover/inlineAdd:opacity-100"></div>
        </div>
        {/* centered + button (visible only on hover of overlay) */}
        <div className="absolute inset-0 flex items-center justify-center">
          <button
            className="w-5 h-5 rounded-full border bg-background shadow text-[12px] leading-4 flex items-center justify-center opacity-0 group-hover/inlineAdd:opacity-100 hover:opacity-100 transition-opacity duration-100 pointer-events-auto"
            title={t('common.pagebuilder.addModule')}
            onClick={(e)=>{ e.stopPropagation(); openAddModal?.({ mode:'sibling', parentId, refChildId, position }); }}
          >
            +
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyAddSlot({ parentId, openAddModal }){
  const { t } = useTranslation();
  return (
    <div className="relative my-2 py-6 group/emptyAdd">
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <button
          className="px-2 h-6 rounded-full border bg-background shadow text-[12px] flex items-center gap-1 opacity-0 group-hover/emptyAdd:opacity-100 hover:opacity-100 transition pointer-events-auto"
          title={t('common.pagebuilder.addModule')}
          onClick={(e)=>{ e.stopPropagation(); openAddModal?.({ mode:'child', parentId }); }}
        >
          <span className="text-base leading-none">+</span>
          <span>{t('common.pagebuilder.addModule')}</span>
        </button>
      </div>
    </div>
  );
}

function AddModuleModal({ onClose, onChoose }){
  const { t } = useTranslation();
  const { allowed } = React.useContext(AllowedContext);
  const items = React.useMemo(() => {
    const list = (allowed || []).map(type => ({ type, ...getModuleMeta(type) }));
    // Stable-ish ordering: category then label
    return list.sort((a, b) => {
      const ca = (a.category || '').toLowerCase();
      const cb = (b.category || '').toLowerCase();
      if (ca !== cb) return ca.localeCompare(cb);
      return (a.label || a.type).toLowerCase().localeCompare((b.label || b.type).toLowerCase());
    });
  }, [allowed]);
  React.useEffect(()=>{
    const onKey = (e)=>{ if(e.key==='Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return ()=> window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center" onClick={onClose}>
      <div className="bg-background rounded-lg shadow-xl w-[520px] max-w-[95vw] max-h-[85vh] flex flex-col" onClick={(e)=> e.stopPropagation()}>
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className="font-semibold text-sm">{t('common.pagebuilder.addModule')}</div>
          <button className="text-xs px-2 py-1 rounded border bg-background hover:bg-slate-50" onClick={onClose}>{t('common.close')}</button>
        </div>
        <div className="p-4 flex-1 min-h-0 overflow-y-auto">
          <div className="grid grid-cols-2 gap-2">
            {items.map(m => (
              <button key={m.type} onClick={()=> onChoose?.(m.type)} className="text-left px-3 py-2 border rounded hover:bg-slate-50">
                <div className="font-medium text-sm leading-tight">{m.label || m.type}</div>
                <div className="text-[10px] text-slate-500 mt-1 flex items-center justify-between">
                  <span>{m.category || 'Andere'}</span>
                  <span className="font-mono opacity-70">{m.type}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Drop({ id, label }){
  const { isOver, setNodeRef } = useDroppable({ id });
  // Keep a minimal hit area even when not hovered so drops can be targeted
  return (
    <div
      ref={setNodeRef}
      className={`${isOver ? 'my-1 h-10 bg-blue-300 outline outline-2 outline-blue-500 rounded flex items-center justify-center' : 'h-4 my-1 bg-slate-100/50'} transition-all`}
      style={{ minHeight: '16px' }}
    >
      {isOver && label && <span className="text-xs text-blue-700 font-medium">{label}</span>}
    </div>
  );
}
